import {isPremiumUser} from '@Utils/user.util';
import {isUserFreelancer, isUserSuper} from '@Libraries/user.library';
import {getActiveStockSeason, SeasonName} from '@Libraries/seasons.library';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import type {StockButtonSubfilter, StockFilter} from '@Panels/stock-panel/stock-panel.types';
import {
  AnimationSubFilter,
  IS_GETTY_ENABLED,
  OrientationSubFilter,
  PricingSubFilter,
  QUALITY_SUBFILTERS,
  StockButtonFilters,
  StockFilterTypes,
  StockPanelTabs,
} from '@Panels/stock-panel/stock-panel.types';

/**
 * Every pricing sub-filter that exists, independent of who is asking. Callers that render chips for the
 * current user want {@link getPricingFilter} instead; this one also serves as the id-to-label dictionary
 * for surfaces that describe another user's logged filters (see manageapp-user-stock-searches), so it
 * must stay tier-agnostic — dropping an entry here would make those labels fall back to raw ids.
 */
export const getPricingSubFilter = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_free'),
      id: PricingSubFilter.FREE,
    },
    {
      text: window.i18next.t('pmwjs_getty_images'),
      id: PricingSubFilter.PREMIUM,
    },
  ];
};

const FREE_PRICING_SUBFILTER_ID: string = PricingSubFilter.FREE;

const isUserBilledPerGettyUse = (): boolean => {
  return isUserFreelancer() || isUserSuper();
};

const hasFreeVsPaidPricingChoice = (): boolean => {
  return !isPremiumUser() || isUserBilledPerGettyUse();
};

const getPricingSubFiltersForCurrentUser = (): StockButtonSubfilter[] => {
  if (!hasFreeVsPaidPricingChoice()) {
    return getPricingSubFilter().filter((subFilter) => subFilter.id !== FREE_PRICING_SUBFILTER_ID);
  }
  return getPricingSubFilter();
};

export const getPricingFilter = (): StockFilter => {
  const pricingFilterName = window.i18next.t('pmwjs_stock_source');
  return {type: StockFilterTypes.BUTTON, name: pricingFilterName, subFilters: getPricingSubFiltersForCurrentUser(), id: StockButtonFilters.PRICING};
};

export const getAnimationSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_animated'),
      id: AnimationSubFilter.ANIMATED,
    },
    {
      text: window.i18next.t('pmwjs_still'),
      id: AnimationSubFilter.STILL,
      testId: PosterEditorTestId.STICKER_STILL_FILTER,
    },
  ];
};

export const getOrientationSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_landscape'),
      id: OrientationSubFilter.LANDSCAPE,
    },
    {
      text: window.i18next.t('pmwjs_portrait'),
      id: OrientationSubFilter.PORTRAIT,
    },
    {
      text: window.i18next.t('pmwjs_square'),
      id: OrientationSubFilter.SQUARE,
    },
  ];
};

const buildOrientationDropdown = (subFilters: StockButtonSubfilter[]): StockFilter => {
  return {type: StockFilterTypes.DROPDOWN, name: window.i18next.t('pmwjs_orientation'), subFilters, id: StockButtonFilters.ORIENTATION};
};

export const getOrientationFilter = (): StockFilter => {
  return buildOrientationDropdown(getOrientationSubFilters());
};

/** Video providers have no square orientation bucket, so Square is omitted for the videos tab. */
export const getOrientationFilterForVideos = (): StockFilter => {
  return buildOrientationDropdown(getOrientationSubFilters().filter((subFilter) => subFilter.id !== OrientationSubFilter.SQUARE.valueOf()));
};

export const getIconSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_colored'),
      id: 'colored',
    },
    {
      text: window.i18next.t('pmwjs_fill'),
      id: 'fill',
    },
    {
      text: window.i18next.t('pmwjs_outline'),
      id: 'outline',
    },
    {
      text: window.i18next.t('pmwjs_hand_drawn'),
      id: 'hand_drawn',
    },
  ];
};

const getShapesSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_shapes_filter_lines'),
      id: 'lines',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_basic'),
      id: 'basic',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_decorative'),
      id: 'decorative',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_labels'),
      id: 'labels',
    },
  ];
};

/**
 * The seasonal pill: a single on/off toggle for whichever season is running, absent out of season
 * and while the feature is switched off. It is the user's control over the seasonal results —
 * on by default for a browse, off by default for a search (see getDefaultSelectedFilterForTab).
 * Only tabs whose catalogue actually has seasonal content carry it.
 */
export const getSeasonalFilter = (): StockFilter[] => {
  const season = getActiveStockSeason();
  if (season === null) {
    return [];
  }

  return [
    {
      id: StockButtonFilters.SEASONAL,
      type: StockFilterTypes.BUTTON,
      name: StockButtonFilters.SEASONAL,
      subFilters: [{id: season, text: getSeasonDisplayName(season)}],
    },
  ];
};

const getSeasonDisplayName = (season: SeasonName): string => {
  switch (season) {
    case SeasonName.HALLOWEEN:
      return window.i18next.t('pmwjs_halloween');
    case SeasonName.CHRISTMAS:
      return window.i18next.t('pmwjs_christmas');
    default:
      throw new Error(`Unhandled season: ${season}`);
  }
};

export const getStockTabsToFilterMapping = (videoQualityFiltersDisabled?: boolean): Record<StockPanelTabs, StockFilter[]> => {
  return {
    [StockPanelTabs.PHOTOS]: [...getSeasonalFilter(), ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []), getOrientationFilter()],
    [StockPanelTabs.SHAPES]: [
      {
        type: StockFilterTypes.BUTTON,
        name: StockPanelTabs.SHAPES,
        subFilters: getShapesSubFilters(),
        id: StockButtonFilters.CATEGORY,
      },
    ],
    [StockPanelTabs.STICKERS]: [
      ...getSeasonalFilter(),
      ...(IS_GETTY_ENABLED && hasFreeVsPaidPricingChoice() ? [getPricingFilter()] : []),
      {type: StockFilterTypes.BUTTON, name: StockButtonFilters.ANIMATION, subFilters: getAnimationSubFilters(), id: StockButtonFilters.ANIMATION},
    ],
    [StockPanelTabs.ICONS]: [{type: StockFilterTypes.BUTTON, name: StockButtonFilters.CATEGORY, subFilters: getIconSubFilters(), id: StockButtonFilters.CATEGORY}],
    [StockPanelTabs.VIDEOS]: [
      ...getSeasonalFilter(),
      ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []),
      {
        type: StockFilterTypes.BUTTON,
        name: window.i18next.t('pmwjs_quality'),
        subFilters: videoQualityFiltersDisabled ? [] : QUALITY_SUBFILTERS,
        id: StockButtonFilters.QUALITY,
      },
      getOrientationFilterForVideos(),
    ],
    // The backend categories (posterbuilder/getStockLibraryAudioCategories) are appended to these
    // by loadAudioCategories in stock-panel.tsx once they arrive.
    [StockPanelTabs.AUDIOS]: getSeasonalFilter(),
    [StockPanelTabs.TEXT_TO_IMAGE]: [],
    [StockPanelTabs.ALL]: [],
  };
};
